import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import {
  listFilterOptions,
  listReviewItems,
  recordBulkDecisions,
  type ListReviewItemsFilters,
} from "@/api/reviewItems";
import { submitLlmBatch } from "@/api/llm";
import type { ReviewItem } from "@/api/types";
import { useToast } from "@/components/toast";
import { itemTypeLabel, langTagLabel, statusLabel } from "./labels";

const PAGE_SIZE = 50;

const STATUS_TABS: Array<{ key: string; label: string }> = [
  { key: "", label: "Все" },
  { key: "open", label: "Открытые" },
  { key: "approved", label: "Принятые" },
  { key: "rejected", label: "Отклонённые" },
  { key: "needs_review", label: "На ревью" },
  { key: "needs_second_review", label: "Второй ревью" },
];

export default function AdminReviewList() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkLlmModel, setBulkLlmModel] = useState<string>(
    "gpt-4o-2024-08-06",
  );

  // URL → filters (single source of truth: query string).
  const filters: ListReviewItemsFilters = {
    status: params.get("status") ?? "open",
    item_type: params.get("item_type") ?? undefined,
    domain: params.get("domain") ?? undefined,
    source_key: params.get("source_key") ?? undefined,
    review_queue: params.get("review_queue") ?? undefined,
    q: params.get("q") ?? undefined,
    limit: PAGE_SIZE,
    offset: Number(params.get("offset") ?? 0) || 0,
  };

  // Локальный input для search'а с debounce, чтобы при наборе не дёргать
  // /admin/review-items на каждом keystroke. На submit (Enter / debounce)
  // обновляем URL ?q=, и useQuery подтягивает новый список.
  const [searchInput, setSearchInput] = useState(params.get("q") ?? "");
  useEffect(() => {
    setSearchInput(params.get("q") ?? "");
  }, [params]);
  useEffect(() => {
    const trimmed = searchInput.trim();
    const current = params.get("q") ?? "";
    if (trimmed === current) return;
    const id = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (trimmed) next.set("q", trimmed);
      else next.delete("q");
      next.delete("offset");
      setParams(next);
    }, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const itemsQuery = useQuery({
    queryKey: ["review-items", filters],
    queryFn: async () => {
      const r = await listReviewItems(filters);
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
  });

  const optsQuery = useQuery({
    queryKey: ["review-filter-options"],
    queryFn: async () => {
      const r = await listFilterOptions();
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    staleTime: 5 * 60_000,
  });

  // Сбрасываем selected при смене фильтров.
  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    setSelected(new Set());
  }, [filtersKey]);

  const items = itemsQuery.data?.items ?? [];
  // Backend пока не возвращает total для review-items; полагаемся на
  // "если страница полная — есть следующая".
  const knownTotal = itemsQuery.data?.total;
  const hasNextPage =
    typeof knownTotal === "number"
      ? (filters.offset ?? 0) + items.length < knownTotal
      : items.length >= PAGE_SIZE;
  const reviewer =
    (auth.user?.profile?.preferred_username as string | undefined) ??
    (auth.user?.profile?.email as string | undefined) ??
    "anonymous";

  const bulkMutation = useMutation({
    mutationFn: async (decision: "approve" | "reject" | "needs_second_review") => {
      const r = await recordBulkDecisions({
        reviewer,
        decision,
        stable_keys: Array.from(selected),
      });
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: (data) => {
      toast(
        `${data.success_count} применено · ${data.error_count} ошибок`,
        data.error_count > 0 ? "warning" : "success",
      );
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ["review-items"] });
    },
    onError: (err: Error) => toast(`Не применено: ${err.message}`, "error"),
  });

  const llmBatchMutation = useMutation({
    mutationFn: async () => {
      const r = await submitLlmBatch({
        review_item_ids: Array.from(selected),
        model_id: bulkLlmModel,
        n_alternatives: 3,
      });
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: (data) => {
      const id = data.batch_job.batch_job_id;
      toast(`Batch создан (${id.slice(0, 8)}) — ${data.pending_suggestion_ids.length} items`, "success");
      setSelected(new Set());
      navigate(`/admin/llm-jobs/${encodeURIComponent(id)}`);
    },
    onError: (err: Error) => toast(`LLM batch: ${err.message}`, "error"),
  });

  function setFilter(key: string, value: string | undefined): void {
    const next = new URLSearchParams(params);
    if (value == null || value === "") next.delete(key);
    else next.set(key, value);
    next.delete("offset");
    setParams(next, { replace: true });
  }
  function setOffset(offset: number): void {
    const next = new URLSearchParams(params);
    if (offset > 0) next.set("offset", String(offset));
    else next.delete("offset");
    setParams(next, { replace: true });
  }
  function resetFilters(): void {
    setParams(new URLSearchParams(), { replace: true });
  }

  function toggleSelect(stable_key: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(stable_key)) next.delete(stable_key);
      else next.add(stable_key);
      return next;
    });
  }
  function toggleSelectAll(): void {
    if (selected.size >= items.length && items.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((it) => it.stable_key)));
    }
  }

  const allChecked = items.length > 0 && selected.size >= items.length;
  const someChecked = selected.size > 0 && !allChecked;

  return (
    <div className="rl-shell">
      {/* SIDEBAR — domains / sources / queues */}
      <aside className="rl-side">
        <div className="rl-side__head">
          <span style={{ fontWeight: "var(--fw-semi)", fontSize: "var(--fs-md)" }}>
            Очереди
          </span>
        </div>
        <div className="rl-side__body">
          <div className="rl-side__title">По домену</div>
          <DomainList
            options={optsQuery.data?.domains ?? []}
            active={filters.domain}
            onPick={(d) => setFilter("domain", d)}
          />
          <div className="rl-side__title">По источнику</div>
          <DomainList
            options={optsQuery.data?.source_keys ?? []}
            active={filters.source_key}
            onPick={(s) => setFilter("source_key", s)}
            mono
          />
          <div className="rl-side__title">По очереди</div>
          <DomainList
            options={optsQuery.data?.review_queues ?? []}
            active={filters.review_queue}
            onPick={(q) => setFilter("review_queue", q)}
          />
        </div>
      </aside>

      {/* MAIN */}
      <main className="rl-main">
        {/* FILTERS */}
        <div className="rl-filters">
          <div className="rl-filters__group">
            <span className="rl-filters__label">Статус</span>
            <div className="mt-segmented">
              {STATUS_TABS.map((t) => (
                <button
                  key={t.key || "all"}
                  className={
                    "mt-segmented__item" +
                    ((filters.status ?? "") === t.key ? " is-active" : "")
                  }
                  onClick={() => setFilter("status", t.key)}
                  type="button"
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="rl-filters__sep" />
          <div className="rl-filters__group">
            <span className="rl-filters__label">Тип</span>
            <select
              className="mt-select mt-input--sm"
              style={{ minHeight: "22px", width: "150px" }}
              value={filters.item_type ?? ""}
              onChange={(e) => setFilter("item_type", e.target.value)}
            >
              <option value="">Все типы</option>
              {(optsQuery.data?.item_types ?? []).map((t) => (
                <option key={t} value={t}>
                  {itemTypeLabel(t)}
                </option>
              ))}
            </select>
          </div>
          <div className="rl-filters__group">
            <span className="rl-filters__label">Домен</span>
            <select
              className="mt-select mt-input--sm"
              style={{ minHeight: "22px", width: "150px" }}
              value={filters.domain ?? ""}
              onChange={(e) => setFilter("domain", e.target.value)}
            >
              <option value="">Все домены</option>
              {(optsQuery.data?.domains ?? []).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <div className="rl-filters__sep" />
          <div className="rl-filters__group" style={{ flex: 1, minWidth: 200 }}>
            <span className="rl-filters__label">Поиск</span>
            <input
              type="search"
              className="mt-input mt-input--sm"
              style={{ minHeight: "22px", flex: 1 }}
              value={searchInput}
              placeholder="EN/RU фраза, stable_key, concept_key"
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Поиск в очереди ревью"
            />
          </div>
          <span className="mt-toolbar__sep" style={{ flex: 1 }} />
          <button
            className="mt-btn mt-btn--sm mt-btn--ghost"
            onClick={resetFilters}
            type="button"
          >
            Сбросить
          </button>
          <div className="mt-divider--v" />
          <button
            className="mt-btn mt-btn--sm mt-btn--secondary"
            type="button"
            disabled={items.length === 0}
            onClick={() => {
              const first = items[0];
              if (!first) return;
              navigate(
                `/admin/review/focus/${encodeURIComponent(first.review_item_id ?? first.stable_key)}?${params.toString()}`,
              );
            }}
          >
            Открыть focus
          </button>
        </div>

        {/* CHIPS */}
        {hasActiveChips(filters) && (
          <div className="rl-chips">
            <ActiveChips filters={filters} onClear={(k) => setFilter(k, undefined)} />
            <span className="mt-toolbar__sep" style={{ flex: 1 }} />
            <span className="rl-chips__count">
              <strong style={{ color: "var(--ink)" }}>
                {typeof knownTotal === "number" ? knownTotal : items.length}
                {typeof knownTotal !== "number" && hasNextPage ? "+" : ""}
              </strong>{" "}
              найдено
              {selected.size > 0 && (
                <>
                  {" · "}
                  <strong style={{ color: "var(--ink)" }}>{selected.size}</strong>{" "}
                  выбрано
                </>
              )}
            </span>
          </div>
        )}

        {/* TABLE */}
        <div className="rl-tablewrap">
          {itemsQuery.isLoading ? (
            <div className="mt-empty">Загружаем очередь…</div>
          ) : itemsQuery.isError ? (
            <div className="mt-empty mt-empty--error">
              {(itemsQuery.error as Error).message}
            </div>
          ) : items.length === 0 ? (
            <div className="mt-empty">Нет задач, подходящих под фильтр.</div>
          ) : (
            <table className="mt-table">
              <thead>
                <tr>
                  <th className="mt-table__check">
                    <label className="mt-check">
                      <input
                        type="checkbox"
                        checked={allChecked}
                        ref={(el) => {
                          if (el) el.indeterminate = someChecked;
                        }}
                        onChange={toggleSelectAll}
                      />
                    </label>
                  </th>
                  <th style={{ width: "80px" }}>Тип</th>
                  <th style={{ width: "160px" }}>Оригинал</th>
                  <th style={{ minWidth: "240px" }}>Перевод</th>
                  <th style={{ width: "120px" }}>Домен</th>
                  <th style={{ width: "130px" }}>Источник</th>
                  <th style={{ width: "90px" }}>Conf</th>
                  <th style={{ width: "140px" }}>Статус</th>
                  <th style={{ width: "100px" }}>Обновлено</th>
                  <th className="mt-td--actions" style={{ width: "56px" }} />
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <Row
                    key={it.stable_key}
                    item={it}
                    selected={selected.has(it.stable_key)}
                    onToggle={() => toggleSelect(it.stable_key)}
                    focusUrl={`/admin/review/focus/${encodeURIComponent(
                      it.review_item_id ?? it.stable_key,
                    )}?${params.toString()}`}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* BULK BAR */}
        {selected.size > 0 && (
          <div className="mt-bulkbar">
            <strong style={{ color: "#fff" }}>{selected.size} выбрано</strong>
            <span
              className="mt-text-tight"
              style={{ color: "rgba(255,255,255,0.6)" }}
            >
              · решения применятся ко всем выбранным
            </span>
            <span className="mt-bulkbar__sep" />
            <button
              className="mt-btn mt-btn--primary"
              onClick={() => bulkMutation.mutate("approve")}
              disabled={bulkMutation.isPending}
              type="button"
            >
              Принять {selected.size}
            </button>
            <button
              className="mt-btn mt-btn--danger"
              onClick={() => bulkMutation.mutate("reject")}
              disabled={bulkMutation.isPending}
              type="button"
            >
              Отклонить {selected.size}
            </button>
            <button
              className="mt-btn"
              onClick={() => bulkMutation.mutate("needs_second_review")}
              disabled={bulkMutation.isPending}
              type="button"
            >
              На второй ревью
            </button>
            <div
              className="mt-divider--v"
              style={{ background: "rgba(255,255,255,0.2)" }}
            />
            <select
              value={bulkLlmModel}
              onChange={(e) => setBulkLlmModel(e.target.value)}
              className="mt-llm__select"
              style={{ marginRight: 6 }}
              disabled={llmBatchMutation.isPending}
              aria-label="Модель LLM для batch"
            >
              <option value="gpt-4o-2024-08-06">GPT-4o (OpenAI)</option>
              <option value="claude-sonnet-4-5">Claude Sonnet 4.5</option>
            </select>
            <button
              className="mt-btn"
              onClick={() => llmBatchMutation.mutate()}
              disabled={llmBatchMutation.isPending}
              type="button"
              title="Batch API — результат через ≤24h, скидка 50%"
            >
              {llmBatchMutation.isPending
                ? "Отправляем…"
                : `Запросить у LLM (${selected.size})`}
            </button>
            <div
              className="mt-divider--v"
              style={{ background: "rgba(255,255,255,0.2)" }}
            />
            <button
              className="mt-btn"
              onClick={() => setSelected(new Set())}
              type="button"
            >
              Отмена
            </button>
          </div>
        )}

        {/* FOOT — pager */}
        <div className="rl-foot">
          <div className="mt-pager">
            <button
              className="mt-icon-btn mt-icon-btn--bordered"
              onClick={() => setOffset(Math.max(0, (filters.offset ?? 0) - PAGE_SIZE))}
              disabled={(filters.offset ?? 0) === 0}
              type="button"
              aria-label="Предыдущая"
            >
              ‹
            </button>
            <span className="mt-pager__count">
              {items.length === 0
                ? "0"
                : `${(filters.offset ?? 0) + 1} — ${(filters.offset ?? 0) + items.length}`}
              {typeof knownTotal === "number" ? ` из ${knownTotal}` : ""}
            </span>
            <button
              className="mt-icon-btn mt-icon-btn--bordered"
              onClick={() => setOffset((filters.offset ?? 0) + PAGE_SIZE)}
              disabled={!hasNextPage}
              type="button"
              aria-label="Следующая"
            >
              ›
            </button>
          </div>
          <span className="rl-foot__sep" />
          <span className="mt-mono mt-text-tight" style={{ fontSize: "var(--fs-xs)" }}>
            {params.toString() || "(без фильтров)"}
          </span>
          {itemsQuery.isFetching && <span className="mt-spinner" />}
        </div>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function DomainList({
  options,
  active,
  onPick,
  mono = false,
}: {
  options: string[];
  active: string | undefined;
  onPick: (val: string | undefined) => void;
  mono?: boolean;
}) {
  if (options.length === 0)
    return <div className="mt-text-tight mt-muted" style={{ padding: "4px 8px" }}>—</div>;
  return (
    <div>
      {options.map((opt) => (
        <div
          key={opt}
          className={"rl-domain" + (active === opt ? " is-active" : "")}
          onClick={() => onPick(active === opt ? undefined : opt)}
        >
          <span className={mono ? "mt-mono mt-text-tight" : undefined}>{opt}</span>
        </div>
      ))}
    </div>
  );
}

function ActiveChips({
  filters,
  onClear,
}: {
  filters: ListReviewItemsFilters;
  onClear: (key: string) => void;
}) {
  const chips: Array<{ key: string; label: string }> = [];
  if (filters.status) chips.push({ key: "status", label: `статус: ${filters.status}` });
  if (filters.item_type)
    chips.push({ key: "item_type", label: `тип: ${filters.item_type}` });
  if (filters.domain) chips.push({ key: "domain", label: `домен: ${filters.domain}` });
  if (filters.source_key)
    chips.push({ key: "source_key", label: `источник: ${filters.source_key}` });
  if (filters.review_queue)
    chips.push({ key: "review_queue", label: `очередь: ${filters.review_queue}` });
  return (
    <>
      {chips.map((c) => (
        <span key={c.key} className="mt-chip">
          {c.label}
          <button
            className="mt-chip__close"
            onClick={() => onClear(c.key)}
            type="button"
          >
            ×
          </button>
        </span>
      ))}
    </>
  );
}

function Row({
  item,
  selected,
  onToggle,
  focusUrl,
}: {
  item: ReviewItem;
  selected: boolean;
  onToggle: () => void;
  focusUrl: string;
}) {
  const navigate = useNavigate();
  const conf = item.confidence ?? null;
  const confClass =
    conf == null
      ? ""
      : conf >= 0.85
        ? " rl-confbar--high"
        : conf < 0.4
          ? " rl-confbar--low"
          : "";
  const confW = conf == null ? 0 : Math.round(conf * 100);

  // Клик по строке (кроме чекбокса) → focus mode.
  function openOnClick(e: React.MouseEvent) {
    const target = e.target as HTMLElement;
    if (target.closest("input,label,a,button")) return;
    navigate(focusUrl);
  }

  return (
    <tr
      className={selected ? "is-selected" : undefined}
      onClick={openOnClick}
      style={{ cursor: "pointer" }}
    >
      <td className="mt-table__check">
        <label className="mt-check">
          <input type="checkbox" checked={selected} onChange={onToggle} />
        </label>
      </td>
      <td>
        <span className={`mt-itemtype mt-itemtype--${item.item_type}`}>
          {shortItemType(item.item_type)}
        </span>
      </td>
      <td>
        <Link
          to={focusUrl}
          className="rl-cell-source"
          style={{ color: "inherit", textDecoration: "none", gap: 6 }}
        >
          {item.source_lang && item.source_lang !== "und" && (
            <span
              className="mt-review__lang-tag"
              style={{ fontSize: 10, flex: "0 0 auto" }}
            >
              {langTagLabel(item.source_lang)}
            </span>
          )}
          <strong>{item.source_text ?? "—"}</strong>
        </Link>
      </td>
      <td>
        <div className="rl-cell-cand" style={{ gap: 6 }}>
          {(() => {
            const target = effectiveTarget(item);
            if (!target) {
              return (
                <span
                  className="mt-text-tight mt-muted"
                  style={{ fontStyle: "italic" }}
                >
                  — нет перевода
                </span>
              );
            }
            return (
              <>
                {item.target_lang && item.target_lang !== "und" && (
                  <span
                    className="mt-review__lang-tag"
                    style={{ fontSize: 10, flex: "0 0 auto" }}
                  >
                    {langTagLabel(item.target_lang)}
                  </span>
                )}
                <span>{target}</span>
              </>
            );
          })()}
        </div>
      </td>
      <td>{item.domain ?? "—"}</td>
      <td className="mt-td--mono mt-text-tight">{item.source_key ?? "—"}</td>
      <td className="mt-td--num">
        {conf == null ? (
          "—"
        ) : (
          <>
            {conf.toFixed(2)}{" "}
            <span
              className={"rl-confbar" + confClass}
              style={{ ["--w" as never]: `${confW}%` } as CSSProperties}
            />
          </>
        )}
      </td>
      <td>
        <span className={`mt-badge ${badgeForStatus(item.status)}`}>
          {statusLabel(item.status)}
        </span>
      </td>
      <td className="mt-td--mono mt-text-tight mt-muted">
        {formatRelative(item.updated_at)}
      </td>
      <td className="mt-td--actions">
        <Link className="mt-icon-btn" to={focusUrl} title="Открыть">
          →
        </Link>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function hasActiveChips(f: ListReviewItemsFilters): boolean {
  return Boolean(
    f.status ||
      f.item_type ||
      f.domain ||
      f.source_key ||
      f.review_queue,
  );
}

/** Возвращает реальный перевод записи (на target_lang) — учитывает разные
 *  раскладки payload в зависимости от item_type / queue. Backend после
 *  edit_and_approve мерджит after_payload в payload, поэтому ru_term/
 *  en_term/target_text могут появиться там же. */
function effectiveTarget(item: ReviewItem): string | undefined {
  const payload = (item.payload ?? {}) as Record<string, unknown>;
  const isRuTarget = item.target_lang === "ru";
  const isEnTarget = item.target_lang === "en";

  // Также копаем во вложенный preferred hint из ru_term_hints (для
  // enforcement_candidate / abbreviation candidates).
  const hints = Array.isArray(payload.ru_term_hints)
    ? (payload.ru_term_hints as Array<unknown>)
    : [];
  const preferredHint = hints.find(
    (h) =>
      h != null &&
      typeof h === "object" &&
      (h as Record<string, unknown>).hint_type === "preferred",
  ) as Record<string, unknown> | undefined;
  const preferredRu =
    preferredHint && typeof preferredHint.ru_term === "string"
      ? (preferredHint.ru_term as string)
      : undefined;

  const ordered: Array<unknown> = [
    // Прямые универсальные ключи (после edit_and_approve / explicit).
    payload.target_text,
    payload.target_term,
    payload.target_expansion,
    payload.target_abbreviation,
    // ОХЛП MNN salt candidates кладут предложенный EN сюда.
    payload.suggested_en_term,
    // Lang-aware fallbacks.
    isRuTarget ? payload.ru_term : undefined,
    isEnTarget ? payload.en_term : undefined,
    // Preferred-hint для enforcement candidates.
    isRuTarget ? preferredRu : undefined,
    // Item-level fields.
    item.candidate_text,
  ];
  for (const c of ordered) {
    if (typeof c === "string" && c !== "" && c !== item.source_text) {
      return c;
    }
  }
  // Canonical_text как последний fallback, но только если != source.
  if (
    typeof item.canonical_text === "string" &&
    item.canonical_text !== "" &&
    item.canonical_text !== item.source_text
  ) {
    return item.canonical_text;
  }
  return undefined;
}

function shortItemType(t: string): string {
  switch (t) {
    case "abbreviation": return "abbr";
    case "glossary_term": return "term";
    case "alias_candidate": return "alias";
    case "term_alias": return "alias";
    case "enforcement_candidate": return "enf";
    case "eval_failure": return "eval";
    default: return t;
  }
}

function badgeForStatus(status: string): string {
  switch (status) {
    case "approved": return "mt-badge--success";
    case "rejected": return "mt-badge--danger";
    case "needs_review":
    case "needs_second_review": return "mt-badge--warning";
    case "open": return "mt-badge--neutral";
    case "merged": return "mt-badge--info";
    case "deprecated": return "mt-badge--neutral";
    default: return "mt-badge--neutral";
  }
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  const m = Math.round(diff / 60_000);
  if (m < 1) return "только что";
  if (m < 60) return `${m} мин`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} ч`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} д`;
  return new Date(iso).toLocaleDateString("ru-RU");
}
